import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EyeOff, Film, Flame, Globe2, ShieldCheck } from "lucide-react";
import { Alert, Button, Checkbox, Empty, Field, Input, LoadingPage, Modal, Select, StatusBadge, Textarea } from "../../components/UI";
import { api, currency, jsonBody } from "../../lib/api";
import { primaryTitle, secondaryTitle } from "../../lib/titles";
import type { Drama } from "../../types";

type AdminData = { dramas: Drama[] };
type PremiereReview = {
  status: string;
  grade: "S" | "A" | "B" | null;
  isPublic: boolean;
  auctionEnabled: boolean;
  premiereStatus: "hidden" | "pending" | "live" | "signed";
  premiereFloorAmount: number | null;
  premiereCurrency: "USD" | "CNY" | "EUR";
  premierePublicNote: string;
  publicExposureOverride: boolean;
  reviewNotes: string;
};

function reviewFromDrama(drama: Drama): PremiereReview {
  return {
    status: drama.status || "reviewing",
    grade: drama.grade,
    isPublic: Boolean(drama.isPublic),
    auctionEnabled: drama.auctionEnabled,
    premiereStatus: drama.premiereStatus || "hidden",
    premiereFloorAmount: drama.premiereFloorAmount,
    premiereCurrency: (drama.premiereCurrency || "CNY") as PremiereReview["premiereCurrency"],
    premierePublicNote: drama.premierePublicNote || "",
    publicExposureOverride: Boolean(drama.publicExposureConfirmed),
    reviewNotes: drama.reviewNotes || ""
  };
}

export function AdminPremieres() {
  const client = useQueryClient();
  const [filter, setFilter] = useState<"pending" | "live" | "all">("pending");
  const [selected, setSelected] = useState<Drama | null>(null);
  const [review, setReview] = useState<PremiereReview | null>(null);
  const query = useQuery({ queryKey: ["admin-dashboard"], queryFn: () => api<AdminData>("/api/admin/dashboard") });
  const save = useMutation({
    mutationFn: () => api("/api/admin/dramas/" + selected!.id + "/review", { method: "PUT", ...jsonBody(review!) }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["admin-dashboard"] }),
        client.invalidateQueries({ queryKey: ["public-premieres"] })
      ]);
      setSelected(null);
      setReview(null);
    }
  });
  const openDrama = (drama: Drama) => {
    setSelected(drama);
    setReview(reviewFromDrama(drama));
  };
  if (query.isLoading) return <LoadingPage />;
  const dramas = (query.data?.dramas || []).filter((drama) => {
    if (filter === "live") return drama.premiereStatus === "live" || drama.premiereStatus === "signed";
    if (filter === "pending") return drama.premiereRequested || drama.premiereStatus === "pending";
    return true;
  });
  return <div className="workspace-page admin-premiere-page">
    <div className="page-heading"><div><span>公开首发</span><h1>首发剧场上架</h1><p>审核公开范围 设置保底参考价并控制首发剧场上线状态</p></div><div className="privacy-chip"><ShieldCheck/><div><strong>双重公开确认</strong><span>内容方授权或平台书面授权确认后才可上线</span></div></div></div>
    <div className="premiere-admin-toolbar">
      <div className="segmented">
        <button className={filter === "pending" ? "active" : ""} onClick={() => setFilter("pending")}>待处理</button>
        <button className={filter === "live" ? "active" : ""} onClick={() => setFilter("live")}>已上线</button>
        <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>全部项目</button>
      </div>
      <span><Flame/>首发剧只展示脱敏公开资料</span>
    </div>
    {dramas.length === 0 ? <Empty title="当前没有对应项目" description="内容方申请首发公开后会进入待处理列表"/> : <div className="premiere-admin-list">
      {dramas.map((drama) => {
        const eligible = drama.aiUnreleased;
        return <button type="button" key={drama.id} onClick={() => openDrama(drama)}>
          <div className="premiere-admin-poster">{drama.posterUrl ? <img src={drama.posterUrl} alt=""/> : <Film/>}</div>
          <div className="premiere-admin-summary"><h2>{primaryTitle(drama.title, drama.titleEn)}</h2>{secondaryTitle(drama.title, drama.titleEn) ? <span>{secondaryTitle(drama.title, drama.titleEn)}</span> : null}<p>{drama.genre} · {drama.format} · {drama.episodeCount} 集 · {drama.completionStatus}</p><small>{eligible ? "全网未首发" : "已首发 / 二轮发行 · " + (drama.previouslyDistributedChannels || "未填写历史平台")}</small></div>
          <div className="premiere-admin-state"><StatusBadge value={drama.premiereStatus}/><strong>{drama.premiereFloorAmount ? currency(drama.premiereFloorAmount, drama.premiereCurrency) : "未设置参考价"}</strong><small>{drama.publicExposureConfirmed ? "内容方已确认公开" : "尚未确认公开授权"}</small></div>
        </button>;
      })}
    </div>}
    {selected && review ? <Modal title={"首发剧场 · " + primaryTitle(selected.title, selected.titleEn)} onClose={() => { setSelected(null); setReview(null); }}>
      <form className="premiere-publish-form" onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
        <div className={selected.aiUnreleased ? "premiere-eligibility eligible" : "premiere-eligibility blocked"}>{selected.aiUnreleased ? <Globe2/> : <EyeOff/>}<div><strong>{selected.aiUnreleased ? "符合首发项目条件" : "该项目不是全网未首发"}</strong><span>{selected.aiUnreleased ? "上线前仍需确认公开授权和保底参考价" : "二轮项目不可上架首发剧场，可改用定向发行资料包"}</span></div></div>
        <div className="form-grid two">
          <Field label="首发剧场状态"><Select value={review.premiereStatus} onChange={(event) => { const premiereStatus = event.target.value as PremiereReview["premiereStatus"]; setReview({ ...review, premiereStatus, isPublic: ["live", "signed"].includes(premiereStatus), auctionEnabled: premiereStatus === "live" }); }} disabled={!selected.aiUnreleased}><option value="hidden">不展示</option><option value="pending">待公开审核</option><option value="live">公开展示并开放报价</option>{selected.premiereStatus === "signed" ? <option value="signed">已签约保底</option> : null}</Select></Field>
          <Field label="内容评级"><Select value={review.grade || ""} onChange={(event) => setReview({ ...review, grade: (event.target.value || null) as PremiereReview["grade"] })}><option value="">未评级</option><option>S</option><option>A</option><option>B</option></Select></Field>
        </div>
        {review.premiereStatus !== "hidden" ? <><div className="form-grid two"><Field label="保底参考价"><Input type="number" min={1} value={review.premiereFloorAmount || ""} onChange={(event) => setReview({ ...review, premiereFloorAmount: event.target.value ? Number(event.target.value) : null })}/></Field><Field label="币种"><Select value={review.premiereCurrency} onChange={(event) => setReview({ ...review, premiereCurrency: event.target.value as PremiereReview["premiereCurrency"] })}><option>CNY</option><option>USD</option><option>EUR</option></Select></Field></div><Field label="首发页公开说明"><Textarea rows={3} value={review.premierePublicNote} onChange={(event) => setReview({ ...review, premierePublicNote: event.target.value })} placeholder="填写可公开的合作窗口 地区或项目亮点"/></Field></> : null}
        <div className="choice-panel compact"><Checkbox label="已取得内容方对剧名 海报 简介及保底信息的书面公开授权" checked={review.publicExposureOverride} onChange={(publicExposureOverride) => setReview({ ...review, publicExposureOverride })}/><small>内容方已在提交页确认时会自动勾选 线下授权需由运营核验后确认</small></div>
        <Field label="内部审核备注" hint="仅运营后台与内容方项目反馈可见"><Textarea rows={3} value={review.reviewNotes} onChange={(event) => setReview({ ...review, reviewNotes: event.target.value })}/></Field>
        {save.error ? <Alert>{save.error.message}</Alert> : null}
        <Button type="submit" className="full-button" loading={save.isPending} disabled={!selected.aiUnreleased || (review.premiereStatus === "live" && (!review.publicExposureOverride || !review.premiereFloorAmount))}><Flame/>保存首发上架状态</Button>
      </form>
    </Modal> : null}
  </div>;
}
